import React, { Component } from "react";
import { Alert, Spinner } from "reactstrap";
import confetti from "canvas-confetti";
import { TesterOutcomeUpload } from "./TestOutcomeUpload";
import { RouteComponentProps } from "react-router";
import {
  MissionType,
  PaymentStatusV1,
  ProviderPaymentCallbackResponse,
  ProviderService,
} from "../generated";
import DOMPurify from "dompurify";

export interface State {
  loading: boolean;
  paymentSuccess: boolean;
  paymentPending: boolean;
  error: string;
  providerErrorMessage: string;
  providerErrorCode: string;
  confetti: boolean;
  paymentStatus: PaymentStatusV1;
  paymentMethod: MissionType;
  paymentId: string;
}

export class PaymentsAuthCallback extends Component<
  RouteComponentProps,
  State
> {
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      loading: true,
      paymentSuccess: false,
      paymentPending: false,
      error: null,
      providerErrorMessage: null,
      providerErrorCode: null,
      confetti: false,
      paymentStatus: null,
      paymentId: null,
      paymentMethod: null,
    };
  }

  updateFromData(data: ProviderPaymentCallbackResponse) {
    this.setState({
      loading: false,
      paymentSuccess: data.success,
      paymentPending: data.pending,
      error: data.error,
      providerErrorMessage: data.providerErrorMessage,
      providerErrorCode: data.providerErrorCode,
      confetti: true,
      paymentStatus: data.status,
      paymentId: data.paymentId,
      paymentMethod: data.paymentMethod,
    });

    if (data.success) {
      // throw a bit of confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: {
          y: 0.6,
        },
      });
    }
  }

  async componentDidMount() {
    const rawPaymentId = new URLSearchParams(this.props.location.search).get(
      "paymentId"
    );
    const sanitizedPaymentId = DOMPurify.sanitize(rawPaymentId);
    const paymentId = sanitizedPaymentId;

    if (paymentId !== null) {
      const encodedPaymentId = encodeURIComponent(paymentId);

      //verify payment method and trigger single payment
      let data = await ProviderService.providerPaymentsCallback({
        paymentId: encodedPaymentId,
      });

      // bulk payment flow
      if (data.getAccessToken) {
        this.setState({
          loading: true,
        });
        // get access token
        await ProviderService.providerAuthenticateCallback({
          code: new URLSearchParams(this.props.location.search).get("code"),
        });

        //create authorization
        data = await ProviderService.providerPaymentsCallback({
          paymentId: encodedPaymentId,
        });

        //redirect to authorize payment
        window.location.href = data.redirectUrl;
        this.setState({
          loading: true,
        });
        return;
      }
      this.updateFromData(data);

      //Updated payment status is not yet ready
      if (data.paymentMethod === "BulkPayments")
        this.setState({
          loading: false,
        });

      let interval = setInterval(async () => {
        if (!this.state.paymentPending) return;

        const data = await ProviderService.providerPaymentsStatus();
        this.updateFromData(data);

        if (data.success) clearInterval(interval);
      }, 2500);
    }
  }

  renderConfetti() {
    if (this.state.paymentPending)
      return (
        <div>
          <h2>
            Are we there yet?{" "}
            <span role="img" aria-label="*wait*">
              ⌚️
            </span>
          </h2>

          <br />

          <p>
            We have sent the payment request, but we are waiting for the final
            verification.
          </p>

          <Alert color="success">
            <Spinner size="sm" /> &nbsp; Hold on, we are checking for an updated
            status.
          </Alert>
        </div>
      );

    if (this.state.paymentSuccess) {
      return (
        <div>
          <h2>
            Mission accomplished!{" "}
            <span role="img" aria-label="*ta da*">
              🎉
            </span>
          </h2>

          <br />
          <p>All done, the payment has been initiated.</p>
          <p>
            <small>You can close this window.</small>
          </p>
          <TesterOutcomeUpload />
        </div>
      );
    } else {
      return (
        <div>
          <h2>
            Whoopsie!{" "}
            <span role="img" aria-label="*head explode*">
              🤯
            </span>
          </h2>
          <br />
          <Alert color="danger">
            Something went wrong while communicating with your provider. Please
            try starting over. If the problem persists, please let us know that
            you experienced this error.
          </Alert>
          <TesterOutcomeUpload />

          <p>
            <small>
              Payment ID is <strong>{this.state.paymentId}</strong>
            </small>
          </p>
          {this.state.paymentStatus && (
            <p>
              <small>
                Payment ended up in the{" "}
                <strong>{this.state.paymentStatus.code}</strong> state
              </small>
            </p>
          )}
          {this.state.providerErrorMessage && (
            <p>
              <small>
                Error message was <strong>{this.state.error}</strong>
              </small>
            </p>
          )}
          {this.state.providerErrorMessage && (
            <p>
              <small>
                Provider message was{" "}
                <strong>{this.state.providerErrorMessage}</strong>
              </small>
            </p>
          )}
          {this.state.providerErrorCode && (
            <p>
              <small>
                Provider code was{" "}
                <strong>{this.state.providerErrorCode}</strong>
              </small>
            </p>
          )}
        </div>
      );
    }
  }

  renderBoring() {
    if (this.state.paymentPending)
      return (
        <div>
          <h2>Verification pending</h2>

          <br />
          <p>
            We have now initiated the payment, but we are awaiting verification
            that the payment has been executed.
          </p>

          <Alert color="success">
            <Spinner size="sm" /> &nbsp; Checking for an updated status.
          </Alert>
        </div>
      );

    if (this.state.paymentSuccess) {
      return (
        <div>
          <h2>Completed</h2>

          <br />
          <p>All done, we have now initiated the payment.</p>
          <p>
            <small>You can close this window.</small>
          </p>
          <TesterOutcomeUpload></TesterOutcomeUpload>
        </div>
      );
    }

    return (
      <div>
        <h2>Something went wrong!</h2>
        <br />
        <Alert color="danger">
          Something went wrong while communicating with your provider. Please
          try starting over. If the problem persists, please let us know that
          you experienced this error.
        </Alert>
        <TesterOutcomeUpload></TesterOutcomeUpload>

        <p>
          <small>
            Error message was <strong>{this.state.error}</strong>
          </small>
        </p>
      </div>
    );
  }

  render() {
    if (this.state.loading) return <p>Loading, please wait...</p>;

    if (this.state.confetti) return this.renderConfetti();

    return this.renderBoring();
  }
}
